import { supabase } from '@/integrations/supabase/client';
import { ApiResult, ok, err, ApiError } from '../errors';
import { mutate } from '../client';
import { profileUpdateSchema } from '@/lib/validations/schemas';
import { sanitizeProfileForDatabase } from '@/lib/utils/dataTransform';

// ============ Types ============

export interface Profile {
  id: string;
  user_id: string;
  // Name fields
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null; // Deprecated, kept for backwards compatibility
  // Contact fields
  phone?: string | null;
  country_code?: string | null;
  // Address fields
  street_address?: string | null;
  street_address_line_2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  address?: string | null; // Deprecated, kept for backwards compatibility
  // Personal information
  date_of_birth?: string | null;
  gender?: string | null;
  // Other fields
  avatar_url?: string | null;
  preferred_language?: string | null;
  group_id?: string | null;
  subgroup_id?: string | null;
  primary_product_id?: string | null;
  invite_code_used?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ProfileUpdate {
  // REQUIRED name fields
  first_name: string;
  last_name: string;
  full_name?: string | null; // Deprecated
  // REQUIRED contact fields
  phone: string;
  country_code: string;
  // REQUIRED address fields
  street_address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  // OPTIONAL address line 2
  street_address_line_2?: string | null;
  address?: string | null; // Deprecated
  // REQUIRED personal information
  date_of_birth: string; // YYYY-MM-DD format
  gender: 'male' | 'female' | 'other' | 'prefer_not_to_say';
  // REQUIRED language preference
  preferred_language: 'en' | 'es' | 'fr' | 'de' | 'pt' | 'it';
  // REQUIRED group ID
  group_id: string; // UUID
  // OPTIONAL fields
  avatar_url?: string | null;
  subgroup_id?: string | null; // UUID
  primary_product_id?: string | null;
}

// ============ Queries ============

export async function getProfile(userId: string): Promise<ApiResult<Profile | null>> {
  const result = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }

  return ok(result.data);
}

export async function getUserPrimaryProduct(userId: string): Promise<ApiResult<string | null>> {
  const result = await supabase
    .from('profiles')
    .select('primary_product_id')
    .eq('user_id', userId)
    .maybeSingle();

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }

  // Default to 'mindcotine' if no primary product is set (backwards compatibility)
  return ok(result.data?.primary_product_id || 'mindcotine');
}

// ============ Phone Validation ============

export async function checkPhoneExists(
  phone: string,
  countryCode: string,
  excludeUserId?: string
): Promise<ApiResult<boolean>> {
  // Use a security definer function to bypass RLS and check across all users
  const result = await supabase.rpc('check_phone_exists', {
    p_phone: phone,
    p_country_code: countryCode,
    p_exclude_user_id: excludeUserId ?? undefined
  });

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }
  
  // Returns true if phone exists (duplicate found)
  return ok(result.data === true);
}

// ============ Phone Sync to auth.users ============

async function syncPhoneToAuthUsers(phone: string, countryCode: string): Promise<void> {
  try {
    const { data: session } = await supabase.auth.getSession();
    if (!session.session?.access_token) {
      console.warn('No session for phone sync');
      return;
    }

    const response = await supabase.functions.invoke('sync-user-phone', {
      body: { phone, country_code: countryCode },
    });

    if (response.error) {
      console.error('Failed to sync phone to auth.users:', response.error);
      // Don't throw - this is a non-critical sync operation
    } else {
      console.log('Phone synced to auth.users successfully');
    }
  } catch (error) {
    console.error('Error syncing phone:', error);
    // Don't throw - profile update should still succeed
  }
}

// ============ Mutations ============

export async function updateProfile(
  userId: string,
  updates: ProfileUpdate
): Promise<ApiResult<Profile>> {
  // Validate with Zod schema
  const validationResult = profileUpdateSchema.safeParse(updates);

  if (!validationResult.success) {
    const errors = validationResult.error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join(', ');
    return err({
      message: `Validation failed: ${errors}`,
      code: 'VALIDATION_ERROR',
      status: 400
    } as ApiError);
  }

  // Check for phone duplication before saving
  const phoneCheckResult = await checkPhoneExists(
    validationResult.data.phone,
    validationResult.data.country_code,
    userId
  );

  if ('error' in phoneCheckResult) {
    return err(phoneCheckResult.error);
  }

  if (phoneCheckResult.data === true) {
    return err({
      message: 'This phone number is already registered to another account',
      code: 'PHONE_DUPLICATE',
      status: 409
    } as ApiError);
  }

  // Sanitize optional fields: "" → null
  const sanitizedUpdates = sanitizeProfileForDatabase(validationResult.data);

  const result: ApiResult<Profile> = await mutate(async () =>
    supabase
      .from('profiles')
      .update(sanitizedUpdates)
      .eq('user_id', userId)
      .select()
      .single()
  );

  // If profile update succeeded, sync phone to auth.users for SMS OTP login
  if ('data' in result && result.data) {
    await syncPhoneToAuthUsers(
      validationResult.data.phone,
      validationResult.data.country_code
    );
  }

  return result;
}

export async function updateLanguagePreference(
  userId: string,
  language: string
): Promise<ApiResult<null>> {
  const result = await supabase
    .from('profiles')
    .update({ preferred_language: language })
    .eq('user_id', userId);

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }
  
  return ok(null);
}

export async function updateAvatarUrl(
  userId: string,
  avatarUrl: string
): Promise<ApiResult<null>> {
  const result = await supabase
    .from('profiles')
    .update({ avatar_url: avatarUrl })
    .eq('user_id', userId);

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }

  return ok(null);
}

